import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Typography,
} from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import { makeStyles } from '@material-ui/core/styles'
import ConsumerCheckoutPlaceOrder from './ConsumerCheckoutPlaceOrder'
import isEmpty from 'lodash/isEmpty'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import { pushToDataLayer } from '../../../utils/dataLayer'
import CheckoutErrorAlert, { ReturnFailureAlert } from './CheckoutErrorAlert'
import SelfGiftingCheckoutError from '../../Messages/SelfGiftingCheckoutError'
import PhoneNumber from './PhoneNumber'
import ColorVisa from '../../Base/Icons/PaymentMethods/ColorVisa'
import ColorMasterCard from '../../Base/Icons/PaymentMethods/ColorMasterCard'
import ColorAmex from '../../Base/Icons/PaymentMethods/ColorAmex'
import ColorDiscover from '../../Base/Icons/PaymentMethods/ColorDiscover'
import { FUNDING } from '@paypal/sdk-constants'
import { PayPalButtons } from '@paypal/react-paypal-js'
import Divider from '@material-ui/core/Divider'
import { MASKED_PHONE_NUMBER_CHAR_COUNT } from '../../../constants'
import { Element, scroller } from 'react-scroll'
import CheckoutSessionPaymentFields from './CheckoutSessionPaymentFields'
import PayPalAccordionRow from './PayPalAccordionRow'
import { validateActiveCheckoutElements } from '../checkoutSessionRuntime'

const useStyles = makeStyles((theme) => ({
    customerInfoPaymentMethodContainer: {
        maxWidth: 500,
        margin: '0 auto',
        top: '0',
        [theme.breakpoints.up('md')]: {
            paddingRight: 40,
            maxWidth: 'none',
            position: 'sticky',
        },
    },
    paymentMethodOption: {
        fontSize: 14,
    },
    h2: {
        fontSize: '1.27rem',
        fontWeight: 'bold',
        marginTop: '1.5rem',
        marginBottom: 0,
        paddingBottom: 14,
        color: theme.palette.primary.main,
    },
    h4: {
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    sectionHeading: {
        marginBottom: '0.75rem',
    },
    paymentMethodHeading: {
        display: 'flex',
        alignItems: 'center',
    },
    paymentMethodIcon: {
        fontSize: '1.9rem',
        marginLeft: '8px',
    },
    paymentMethodContainer: {
        marginBottom: 12,
        backgroundColor: 'white',
        minWidth: 300,
        width: '100%',
        position: 'unset', // prevent stacking context so it doesn't show in front of processing backdrop
    },
    paymentFields: {
        visibility: 'visible',
        minWidth: '269px',
        padding: '8px 0px',
        fontSize: '1rem',
        outline: 0,
        borderRadius: '4px',
        border: 'none',
        background: 'white',
        display: 'flex',
        flexWrap: 'wrap',
        margin: '0',
        justifyContent: 'space-between',
        [theme.breakpoints.up('md')]: {
            maxWidth: 555,
        },
        '&:focused': {
            boxShadow:
                'rgba(50, 50, 93, 0.109804) 0 4px 6px, rgba(0, 0, 0, 0.0784314) 0 1px 3px',
            '-webkit-transition': 'all 150ms ease',
            transition: 'all 150ms ease',
        },
        '&:last-of-type': {
            '& label': {
                minWidth: '42px',
                [theme.breakpoints.up('sm')]: {
                    minWidth: '90px',
                },
            },
        },
    },
    noPaddingBottom: {
        paddingBottom: 0,
    },
    noPaddingTop: {
        paddingTop: 0,
    },
    errorMessageContainer: {
        [theme.breakpoints.up('md')]: {
            maxWidth: 500,
        },
    },
    paymentFormSeperator: {
        margin: '31px 0',
        backgroundColor: '#e7e7e7',
        border: '0',
        height: 2,
        [theme.breakpoints.up('md')]: {
            maxWidth: 555,
        },
    },
    cardElementContainer: {
        display: 'block',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        width: '100%',
        position: 'relative',
    },
    cardElement: {
        flex: 1,
        display: 'block',
        padding: '28px 23px',
        fontSize: '36px',
        fontFamily: 'Source Code Pro, monospace',
        boxShadow:
            'rgba(50, 50, 93, 0.14902) 0 1px 3px, rgba(0, 0, 0, 0.0196078) 0 1px 0',
        border: '2.5px solid #8a8a8a',
        outline: 0,
        borderRadius: '60px',
        height: '64px',
        '&:focused': {
            boxShadow:
                'rgba(50, 50, 93, 0.109804) 0 4px 6px, rgba(0, 0, 0, 0.0784314) 0 1px 3px',
            '-webkit-transition': 'all 150ms ease',
            transition: 'all 150ms ease',
        },
        '&::placeholder': {
            color: '#aab7c4',
        },
        '& label': {
            marginTop: '10px',
        },
    },
    cardElementLabel: {
        position: 'absolute',
        left: '25px',
        top: '50%',
        transform: 'translateY(-50%)',
        transition: 'all 0.2s ease',
        pointerEvents: 'none',
    },
    labelFloating: {
        top: '15px',
        left: '25px',
        fontSize: '0.8em',
        color: '#999',
    },
    AddPaymentRadioSection: {
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        '& svg': {
            height: 16,
            width: 24,
        },
    },
    bigRadio: {
        '& svg': {
            height: '2.2rem',
            width: '2.2rem',
        },
    },
    payPalButton: {
        display: 'block',
        margin: '0 0 0.75rem 0px',
        borderRadius: 60,
        border: '0px solid',
        width: '100%',
        backgroundColor: '#FFC43A',
        overflow: 'hidden',
        height: 50,
        [theme.breakpoints.up('md')]: {
            maxWidth: 555,
        },
    },
    blue: {
        color: '#0099FF',
    },
    explanationText: {
        fontSize: '0.8rem',
        padding: '0 1.4rem',
    },
    accountDetail: {
        fontWeight: 'bold',
        fontSize: '1.1rem',
        color: '#444',
        marginBottom: '0.25rem',
        padding: '0px 7px',
        '&:last-of-type': {
            marginBottom: '0.45rem',
        },
    },
    closeButton: {
        position: 'absolute',
        color: 'white',
        backgroundColor: '#6f6f6f',
        right: 0,
        padding: 0,
    },
}))

const ConsumerCheckoutForm = ({
    baseUrl,
    canPlaceOrder,
    isPaypalCheckout,
    handlePaypalCreateOrder,
    handlePaypalOnApprove,
    handlePaypalOnError,
    handlePaypalOnCancel,
    user,
    anyPaymentMethodsExist,
    billingDetails,
    setBillingDetails,
    cvv,
    setCvv,
    validateField,
    errors,
    setErrors,
    errorCode,
    billingErrors,
    processing,
    setProcessing,
    showCreditCardForm,
    showPlaceOrderButton,
    handlePlaceOrder,
    validateCreditCardForm,
    setBillingErrors,
    onPaymentFormLoadError,
    checkoutSessionReady,
    checkoutSessionLoadFailed,
    resetUpdatePaymentMethodErrors,
    cartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold,
    errorSelfGifting,
    errorPhone,
    errorCvv,
    showPhoneInput,
    setErrorPhone,
    checkingPhone,
    setCheckingPhone,
    phoneCheckSuccess,
    setPhoneCheckSuccess,
    clearErrors,
    handlePaypalOnClick,
    options,
    showStripe,
    setErrorCvv,
    handleErrorAlertClick,
    showCardError,
    setShowCardError,
    returnFailureMessage,
    onDismissReturnFailure,
    setSelectedPaymentElement,
    selectedPaymentElement,
}) => {
    // CON-9505: the billing address element reports its values through onChange; unlike the classic
    // AddressElement there is no getValue() to read them back at submit time.
    const formRef = useRef()
    const classes = useStyles()

    const [canHandlePlaceOrder, setCanHandlePlaceOrder] = useState(false)
    const [paypalButtonRefresher, setPaypalButtonRefresher] = useState(true)
    const [paymentElement, setPaymentElement] = useState('')
    const [isPayPalOpen, setIsPayPalOpen] = useState(false)
    // Reported to the fields component, which owns the gate; nothing out here reads it.
    const [, setStripeElementReady] = useState(false)
    const paymentFieldsRef = useRef(null)
    const [
    ] = useState(false)

    const [previousPhoneNumber, setPreviousPhoneNumber] = useState('')

    const getAddressDataRef = useRef()
    getAddressDataRef.current = useCallback(() => {
        return {
            address1: billingDetails.address1,
            city: billingDetails.city,
            region: billingDetails.region,
            postalCode: billingDetails.postalCode,
        }
    }, [billingDetails])

    useEffect(() => {
        // Hack part one. This is a hack to re-render the PayPalButtons component
        // every time billingDetailsChanges. The createOrder function
        // gets cached at the time of render, so this is the only way
        // we've found to accomplish having the phone number in the handlePaypalCreateOrder function.
        // This causes the paypal button to flash, so we only want to update it when the use reaches a full phone number
        setPreviousPhoneNumber(billingDetails.phone)
        if (errorPhone) {
            setPaypalButtonRefresher(false)
            return
        }
        if (
            billingDetails?.phone?.length >= MASKED_PHONE_NUMBER_CHAR_COUNT ||
            (previousPhoneNumber?.length >= MASKED_PHONE_NUMBER_CHAR_COUNT &&
                billingDetails?.phone?.length < MASKED_PHONE_NUMBER_CHAR_COUNT)
        ) {
            setPaypalButtonRefresher(false)
        }
        // eslint-disable-next-line
    }, [billingDetails.phone, errorPhone])

    useEffect(() => {
        // Hack part two. paypalButtonRefresher must be toggled back to true
        // in a separate useEffect.
        setPaypalButtonRefresher(true)
        // eslint-disable-next-line
    }, [paypalButtonRefresher])

    const scrollerConfig = useMemo(
        () => ({
            duration: 0,
            delay: 0,
            smooth: false,
            offset: -120,
        }),
        []
    )

    const submitEventListener = async (e) => {
        e.preventDefault()
        // Asked first so Stripe's field errors show in the same click as a phone error.
        const elementsAreValid = await validateActiveCheckoutElements()
        let doScrollAndReturn = false
        const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/
        if (errorPhone) {
            // This early scroll and return is intentional It handles the case where the phone deliverability check created an error
            scroller.scrollTo('emailAndPhoneElement', scrollerConfig)
            return
        }
        if (!phoneRegex.test(billingDetails.phone)) {
            setErrorPhone('Please enter a valid phone number.')
            doScrollAndReturn = true
        } else {
            setErrorPhone('')
        }

        if (doScrollAndReturn) {
            scroller.scrollTo('emailAndPhoneElement', scrollerConfig)
            return
        }


        pushToDataLayer(
            ['CheckoutClick', 'Checkout', 'Click', 'Submit Order'],
            { type: 'stripe' }
        )

        if (!elementsAreValid) {
            scroller.scrollTo('cardFormElements', scrollerConfig)
            return
        }

        if (await canPlaceOrder()) {
            setCanHandlePlaceOrder(true)
        } else {
        }
    }

    useEffect(() => {
        if (canHandlePlaceOrder && showStripe) {
            setCanHandlePlaceOrder(false)
            handlePlaceOrder()
        }
    }, [canHandlePlaceOrder, handlePlaceOrder, showStripe])

    return (
        <Grid
            item
            xs={12}
            md={6}
            className={classes.customerInfoPaymentMethodContainer}
        >
            <Box>
                <Typography
                    variant="h2"
                    className={classes.h2}
                    data-testid="logged-in-customer-info-headline"
                >
                    Account Details
                </Typography>
                <Box
                    className={classes.accountDetail}
                    display="flex"
                    alignItems="center"
                    data-testid="logged-in-customer-name"
                >
                    {user.full_name}
                </Box>
                <Box
                    className={classes.accountDetail}
                    display="flex"
                    alignItems="center"
                    data-testid="logged-in-customer-email"
                >
                    {user.username}
                </Box>
                <Element name="emailAndPhoneElement" />
                {(errorPhone || !isEmpty(billingErrors)) && (
                    <Typography variant={'body1'} style={{ color: '#D74242' }}>
                        Please enter all contact information
                    </Typography>
                )}
                {showPhoneInput && (
                    <PhoneNumber
                        billingDetails={billingDetails}
                        setBillingDetails={setBillingDetails}
                        errorPhone={errorPhone}
                        setErrorPhone={setErrorPhone}
                        validateOnType={true}
                        checkingPhone={checkingPhone}
                        setCheckingPhone={setCheckingPhone}
                        phoneCheckSuccess={phoneCheckSuccess}
                        setPhoneCheckSuccess={setPhoneCheckSuccess}
                        clearErrors={clearErrors}
                        setShowCardError={setShowCardError}
                        labelColor={'#222'}
                        errorBorderWidth={3}
                        errorFontSize={'.85rem'}
                    />
                )}
            </Box>
            <Typography
                varient={'body1'}
                className={`${classes.explanationText}`}
            >
                Required for account security.
            </Typography>
            <Divider className={classes.paymentFormSeperator} />
            <Typography
                variant="h2"
                className={`${classes.h2} ${classes.paymentMethodHeading}`}
                data-testid="logged-in-payment-method-headline"
            >
                Select payment method
            </Typography>

            <Element name="cardFormElements" />

            {showCreditCardForm && showStripe && (
                <form ref={formRef} onSubmit={submitEventListener}>
                    <fieldset className={classes.paymentFields}>
                        <div className={`${classes.cardElementContainer}`}>
                            <CheckoutSessionPaymentFields
                                ref={paymentFieldsRef}
                                checkoutSessionReady={checkoutSessionReady}
                                checkoutSessionLoadFailed={checkoutSessionLoadFailed}
                                onFocus={() => setShowCardError(false)}
                                onSelectionChange={(selection) => {
                                    setSelectedPaymentElement(
                                        selection?.type ?? ''
                                    )
                                    setPaymentElement(selection?.type ?? '')
                                }}
                                onLoadError={onPaymentFormLoadError}
                                onInteract={() => setIsPayPalOpen(false)}
                                onElementReady={setStripeElementReady}
                                payPalOpen={isPayPalOpen}
                                payPalRow={
                                    paypalButtonRefresher &&
                                    !cartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold ? (
                                        <PayPalAccordionRow
                                            open={isPayPalOpen}
                                            onToggle={(open) => {
                                                setIsPayPalOpen(open)
                                                if (open) {
                                                    // Opening PayPal is a new payment attempt: a decline
                                                    // left over from a card attempt otherwise stays on
                                                    // screen underneath the PayPal button, and the Stripe
                                                    // rows collapse so the list behaves as one accordion.
                                                    clearErrors()
                                                    paymentFieldsRef.current?.collapse()
                                                }
                                            }}
                                        >
                                            <div
                                                className={classes.payPalButton}
                                                data-testid="paypal-checkout-button-container"
                                            >
                                                <PayPalButtons
                                                    style={{
                                                        shape: 'pill',
                                                        height: 55,
                                                    }}
                                                    fundingSource={FUNDING.PAYPAL}
                                                    onClick={handlePaypalOnClick}
                                                    createOrder={handlePaypalCreateOrder}
                                                    onApprove={handlePaypalOnApprove}
                                                    onError={handlePaypalOnError}
                                                    onCancel={handlePaypalOnCancel}
                                                />
                                            </div>
                                        </PayPalAccordionRow>
                                    ) : null
                                }
                            />
                        </div>
                    </fieldset>
                    <ReturnFailureAlert
                        message={returnFailureMessage}
                        onClose={onDismissReturnFailure}
                    />
                    {/* Separate anchor, not the alert itself: submit scrolls in the same tick the
                        error state is set, before the alert has mounted. */}
                    <Element name="checkoutErrorAlert" />
                    <CheckoutErrorAlert
                        errors={errors}
                        errorCode={errorCode}
                        showCardError={showCardError}
                        handleClick={handleErrorAlertClick}
                    />
                    <SelfGiftingCheckoutError open={errorSelfGifting} />
                    {/* Must be inside form for submit listener */}
                    <hr className={classes.paymentFormSeperator} />
                    <ConsumerCheckoutPlaceOrder
                        diagnosticInfo={{
                            caller: 'non-guest, Stripe payment form',
                            showCreditCardForm,
                            showPlaceOrderButton,
                            billingDetails,
                            billingErrors,
                            errorPhone,
                            phoneCheckSuccess,
                            isPaypalCheckout,
                        }}
                        type="submit"
                        baseUrl={baseUrl}
                        processing={processing}
                        error={billingErrors && billingErrors.length > 0}
                        disableSubmit={
                            !showPlaceOrderButton ||
                            errorPhone ||
                            !phoneCheckSuccess
                        }
                        placeOrderButtonText={'Submit Order'}
                        // An open PayPal row makes PayPal's own button the order action, so
                        // the submit button yields (the terms text stays) until a Stripe row
                        // is picked again.
                        isPaypalCheckout={isPaypalCheckout || isPayPalOpen}
                        handlePaypalOnClick={handlePaypalOnClick}
                        handlePaypalCreateOrder={handlePaypalCreateOrder}
                        handlePaypalOnApprove={handlePaypalOnApprove}
                        handlePaypalOnError={handlePaypalOnError}
                        handlePaypalOnCancel={handlePaypalOnCancel}
                        billingDetails={billingDetails}
                        errorPhone={errorPhone}
                        checkingPhone={checkingPhone}
                    />
                </form>
            )}

            {/* Existing payment method, PayPal or Apple/Google Pay in use */}
            {!showCreditCardForm && (
                <>
                    <CheckoutErrorAlert
                        errors={errors}
                        errorCode={errorCode}
                        showCardError={showCardError}
                        handleClick={handleErrorAlertClick}
                    />

                    <SelfGiftingCheckoutError open={errorSelfGifting} />
                    <Divider className={classes.paymentFormSeperator} />
                    <ConsumerCheckoutPlaceOrder
                        diagnosticInfo={{
                            caller: 'non-guest, Existing payment method, PayPal or Apple/Google Pay in use',
                            showCreditCardForm,
                            showPlaceOrderButton,
                            billingDetails,
                            billingErrors,
                            errorPhone,
                            phoneCheckSuccess,
                            isPaypalCheckout,
                        }}
                        baseUrl={baseUrl}
                        processing={processing}
                        error={billingErrors && billingErrors.length > 0}
                        disableSubmit={
                            !showPlaceOrderButton ||
                            errorPhone ||
                            !phoneCheckSuccess
                        }
                        onSubmit={async (e) => {
                            pushToDataLayer([
                                'CheckoutClick',
                                'Checkout',
                                'Click',
                                'Submit Order',
                            ])
                            const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/
                            if (errorPhone) {
                                scroller.scrollTo(
                                    'emailAndPhoneElement',
                                    scrollerConfig
                                )
                                return
                            }
                            if (!phoneRegex.test(billingDetails.phone)) {
                                setErrorPhone(
                                    'Please enter a valid phone number.'
                                )
                                scroller.scrollTo(
                                    'emailAndPhoneElement',
                                    scrollerConfig
                                )
                                return
                            } else {
                                setErrorPhone('')
                            }
                            await handlePlaceOrder()
                        }}
                        placeOrderButtonText={'Submit Order'}
                        isPaypalCheckout={isPaypalCheckout}
                        handlePaypalCreateOrder={handlePaypalCreateOrder}
                        handlePaypalOnApprove={handlePaypalOnApprove}
                        handlePaypalOnError={handlePaypalOnError}
                        handlePaypalOnCancel={handlePaypalOnCancel}
                        billingDetails={billingDetails}
                        checkingPhone={checkingPhone}
                        errorPhone={errorPhone}
                    />
                </>
            )}
        </Grid>
    )
}

export default ConsumerCheckoutForm
